import {
	SenderSource,
	IChatTextMessage,
	IChatCommandWidgetMessage,
	IChatCommandWidgetHtmlData,
} from '@enuan/chat__utils';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { escape as _escape, unescape as _unescape } from 'lodash';
import { parseText } from '../../../utils/text';
import { useMessageContext } from '../message.context';
import { Time } from '../Time';
import { useIntl } from 'react-intl';
import { genericMessages } from '../../../generic.i18n';
import { UnreadMessageIcon } from '../../UI/Message/components/UnreadMessageIcon';
import { styled } from '@mui/material/styles';

type TextProps = {
	className?: string;
};

/**
 * Renders text messages as parsed Markdown-safe message markup.
 *
 * The component reads the current text message from context, escapes user text
 * before Markdown parsing, and updates local state after asynchronous parsing.
 *
 * @param props - Root styling props injected by the styled wrapper.
 * @returns Parsed text content with optional inline delivery metadata.
 */
export const Text = styled(
	({ className }: TextProps) => {
		const { message } = useMessageContext<IChatTextMessage>();
		const {
			timestamp,
			sender,
			_meta: { sent },
		} = message;
		const hasInlineFooter = sender !== SenderSource.USER;

		const [parsedText, setParsedText] = useState<string | undefined>(() =>
			parsedTextCache.get(message.client_id),
		);
		useEffect(() => {
			if (parsedText !== undefined) return;
			let cancelled = false;
			/**
			 * Converts the current text message into renderable HTML.
			 *
			 * The callback reads only the current message and updates component state
			 * unless the effect has already been cleaned up.
			 */
			const parse = async () => {
				const safe = _escape(message._data);
				const result = await parseText(safe);
				if (cancelled) return;

				parsedTextCache.set(message.client_id, result);
				setParsedText(result);
			};
			parse();
			return () => {
				cancelled = true;
			};
		}, [message, parsedText]);

		const isSendindMessage =
			!!sent && !timestamp && sender === SenderSource.USER;

		const isInline =
			!parsedText?.includes('<img') &&
			!parsedText?.includes('<iframe') &&
			hasInlineFooter;

		if (typeof parsedText === 'undefined') return null;

		return (
			<TextBase
				className={className}
				hasInlineFooter={hasInlineFooter}
				isInline={isInline}
				isSending={isSendindMessage}
			>
				<div
					className="text__content"
					dangerouslySetInnerHTML={{ __html: parsedText }}
				></div>
			</TextBase>
		);
	},
	{
		target: 'Text-root',
		label: 'Text',
	},
)({});

type HtmlProps = {
	className?: string;
};

/**
 * Renders HTML widget command messages without Markdown parsing.
 *
 * The component reads only HTML widget commands from context and injects the
 * normalized provider markup directly, avoiding Markdown code-block conversion.
 *
 * @param props - Root styling props injected by the styled wrapper.
 * @returns HTML widget content with optional inline delivery metadata.
 */
export const Html = styled(
	({ className }: HtmlProps) => {
		const { message } =
			useMessageContext<IChatCommandWidgetMessage<IChatCommandWidgetHtmlData>>();
		const {
			sender,
			_data: {
				parameters: { data },
			},
		} = message;
		const html = normalizeCommandHtmlContent(data);
		const hasInlineFooter = sender !== SenderSource.USER;
		const isInline =
			!html.includes('<img') && !html.includes('<iframe') && hasInlineFooter;

		return (
			<TextBase
				className={className}
				hasInlineFooter={hasInlineFooter}
				isInline={isInline}
			>
				<div
					className="text__content"
					dangerouslySetInnerHTML={{ __html: html }}
				></div>
			</TextBase>
		);
	},
	{
		target: 'Html-root',
		label: 'Html',
	},
)({});

type TextBaseProps = {
	children: React.ReactNode;
	className?: string;
	hasInlineFooter: boolean;
	isInline: boolean;
	isSending?: boolean;
};

/**
 * Provides the shared message text layout and footer behavior for text-like content.
 *
 * The component owns the static styles reused by parsed text and raw HTML widget
 * renderers, and appends inline footer placeholders when the sender requires them.
 *
 * @param props - Shared text layout state and rendered content.
 * @returns Styled text-like message body.
 */
const TextBase = styled(
	({
		children,
		className,
		hasInlineFooter,
		isInline,
		isSending,
	}: TextBaseProps) => (
		<div
			className={classNames(
				className,
				'message__text',
				isSending && 'message__text--sending',
				hasInlineFooter && 'message__text--withfooter',
				isInline && 'message__text--inline',
			)}
		>
			{children}
			{hasInlineFooter && (
				<>
					<TextFooter placeholder />
					<TextFooter />
				</>
			)}
		</div>
	),
	{
		target: 'TextBase-root',
		label: 'TextBase',
	},
)({
	fontFamily: 'Roboto, sans-serif',
	fontWeight: 400,
	fontSize: '1.3em',
	color: 'var(--message_color)',

	'& .text__content': {
		'& ul': {
			paddingInlineStart: '1.5em',
		},
	},
	'& .iframe-container': {
		position: 'relative',

		paddingBottom: '56.25%',
		height: 0,
		width: '100vw',
		maxWidth: 'calc(100% - 1em)',

		margin: '0.5em',

		overflow: 'hidden',

		'&:has(.message--left >)': {
			borderTopRightRadius: '0.6em',
			borderBottomRightRadius: '0.6em',
			borderBottomLeftRadius: '0.6em',
			borderTopLeftRadius: '0',
		},
		'&:has(.message--right >)': {
			borderTopLeftRadius: '0.6em',
			borderBottomLeftRadius: '0.6em',
			borderBottomRightRadius: '0.6em',
			borderTopRightRadius: '0',
		},

		'& iframe': {
			top: 0,
			left: 0,
			position: 'absolute',
		},
	},

	'& .image': {
		maxWidth: 'calc(100% - 1em)',
		margin: '0.5em',
		':has(.message--left >)': {
			borderTopRightRadius: '0.6em',
			borderBottomRightRadius: '0.6em',
			borderBottomLeftRadius: '0.6em',
			borderTopLeftRadius: 0,
		},
		':has(.message--right >)': {
			borderTopLeftRadius: '0.6em',
			borderBottomLeftRadius: '0.6em',
			borderTopRightRadius: '0.6em',
			borderBottomRightRadius: 0,
		},
	},
	'&.message__text--sending': {
		opacity: 0.7,
	},
	'&.message__text--withfooter': {
		position: 'relative',
		'&.message__text--inline': {
			'& .text__content, & .text__content p:last-child': {
				display: 'inline',
			},
		},
		'& .text__footer': {
			display: 'inline-flex',
			alignItems: 'center',
			fontSize: '80%',
			'& .message__unread-icon': {
				display: 'inline-flex',
				'&:before': {
					margin: 0,
					marginLeft: '0.4em',
				},
			},

			'&.placeholder': {
				visibility: 'hidden',
				marginLeft: '0.5em',
			},
			'&:not(.placeholder)': {
				position: 'absolute',
				bottom: '-0.5em',
				right: '-0.2em',
			},
		},
	},
});

const parsedTextCache = new Map<string, string>();

/**
 * Restores provider HTML widget payloads only when their tags were escaped before delivery.
 *
 * @param value - Raw `show_widget` HTML command content received from the provider.
 * @returns HTML content with escaped tags decoded, or the original content when it is already renderable.
 */
const normalizeCommandHtmlContent = (value: string): string => {
	if (!hasEscapedHtmlTags(value)) return value;

	return _unescape(value);
};

/**
 * Detects whether the payload contains encoded HTML tag delimiters.
 *
 * @param value - Raw HTML widget content to inspect.
 * @returns Whether the content appears to contain escaped HTML tags.
 */
const hasEscapedHtmlTags = (value: string): boolean =>
	/&lt;\/?[a-z][^>]*?&gt;/i.test(value);

type TextFooterProps = {
	placeholder?: boolean;
};

/**
 * Renders timestamp and unread status metadata for non-user messages.
 *
 * The component reads message state and localized labels from context, and can
 * render a hidden placeholder copy to reserve inline footer space.
 *
 * @param props - Footer rendering options.
 * @returns Inline footer metadata for the current message.
 */
const TextFooter = ({ placeholder }: TextFooterProps) => {
	const intl = useIntl();
	const unreadMessage = intl.formatMessage(genericMessages.unread);
	const {
		message: {
			timestamp,
			_meta: { read },
		},
	} = useMessageContext<
		IChatTextMessage | IChatCommandWidgetMessage<IChatCommandWidgetHtmlData>
	>();
	return (
		<>
			<span
				className={classNames('text__footer', placeholder && 'placeholder')}
			>
				<Time timestamp={timestamp} />
				{!read && <UnreadMessageIcon title={unreadMessage} />}
			</span>
		</>
	);
};
